import classNames from "classnames";
import { lessThan } from "dinero.js";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { defaults } from "../../apps/configurator/defaults";
import { setOptionValue } from "../../apps/configurator/reducers";
import {
    productAttributeOptionGroupSelector,
    rootProductSelector,
} from "../../apps/configurator/selectors";
import { ConfiguratorTypes, OptionTypes } from "../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
    IProductAttributeOptionGroup,
    InternalProduct,
} from "../../models/catalogue";
import { IImageURL } from "../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../store/reducers";
import {
    buildRelevantLevelVariants,
    findLevelVariants,
    getProductVariantsForOptionCodes,
} from "../../store/selectors";
import { PriceType, getProductPrice } from "../../utils/catalogue";
import { roundedMoney } from "../../utils/format";
import { usePageSetting } from "../../utils/settings";
import { listPossibleOptionValues } from "../../utils/sorting";
import { Image } from "../Image";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    configuratorType?: ConfiguratorTypes;
    selectorID: string;
    rootProduct: IProduct;
    code: IOptionCode;
    value?: IOptionSingleValue;
    options: IOptionSingleValue[];
    prefixNameCapped: string;
    configuratorClass: string;
    prefixClass: string;
    onChange: (event: React.FormEvent<HTMLInputElement>) => void;
    isIcon?: boolean;
    showPricing?: boolean;
    isPlpPanelStyle?: boolean;
}>;

function buildUnavailableOverlay(
    showUnavailableLabel: boolean,
): React.ReactNode {
    if (!showUnavailableLabel) {
        return null;
    }
    return (
        <div
            className={`${styles.pricingOverlay} ${styles.unavailableOverlay}`}
        >
            <span className={styles.unavailableLabel}>{t`Not Available`}</span>
        </div>
    );
}

function buildPricingOverlay(
    variant: IProduct | undefined,
    showPricing: boolean | undefined,
): React.ReactNode {
    if (!showPricing || !variant || !variant.availability.is_available_to_buy) {
        return null;
    }
    const retailPrice = getProductPrice(variant.price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const cosmeticPrice = getProductPrice(variant.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    if (lessThan(cosmeticPrice, retailPrice)) {
        return (
            <div className={styles.pricingOverlay}>
                <span className={styles.actualPrice}>
                    <span className="ada-screenreader-only">
                        {t`Current Price:`}
                    </span>
                    {roundedMoney(cosmeticPrice)}
                </span>
                <span className={styles.retailPrice}>
                    <span className="ada-screenreader-only">
                        {t`Original Price:`}
                    </span>
                    {roundedMoney(retailPrice)}
                </span>
            </div>
        );
    }
    return (
        <div className={styles.pricingOverlay}>
            <span>{roundedMoney(retailPrice)}</span>
        </div>
    );
}

function buildLabelContent(
    iconImageURL: IImageURL | null,
    isPanel: boolean,
    option: string,
    isPlpPanelStyle: boolean | undefined,
    pricingOverlay?: React.ReactNode,
): React.ReactNode {
    const icon = iconImageURL && isPanel && (
        <Image className={styles.icon} src={iconImageURL} alt="" />
    );
    if (pricingOverlay && isPlpPanelStyle) {
        return (
            <>
                <div>
                    {icon}
                    {option}
                </div>
                {pricingOverlay}
            </>
        );
    }
    if (pricingOverlay) {
        return (
            <>
                {icon}
                <span className={styles.labelDetails}>
                    {option}
                    {pricingOverlay}
                </span>
            </>
        );
    }
    const content = (
        <>
            {icon}
            {option}
        </>
    );
    return isPlpPanelStyle ? <div>{content}</div> : content;
}

function preferAvailableVariant(
    variants: InternalProduct[],
): InternalProduct | undefined {
    if (variants.length <= 1) {
        return variants[0];
    }
    return (
        variants.find((v) => v.availability.is_available_to_buy) ?? variants[0]
    );
}

function getCodeVariants(
    options: readonly IOptionSingleValue[],
    rootProduct: IProduct,
    code: IOptionCode,
    currentOptionValues: IOptionValues,
): Record<IOptionSingleValue, InternalProduct[]> {
    return options.reduce(
        (variants: Record<IOptionSingleValue, InternalProduct[]>, option) => {
            const mergedValues: IOptionValues = {
                ...currentOptionValues,
                [code]: option,
            };
            const codes = Object.keys(mergedValues) as IOptionCode[];
            variants[option] = getProductVariantsForOptionCodes(
                [rootProduct],
                codes,
                mergedValues,
            );
            return variants;
        },
        {},
    );
}

function getLabelConfig(
    rootProduct: IProduct,
    code: IOptionCode,
    prefixNameCapped: string,
    options: readonly IOptionSingleValue[],
): Array<{
    label: string;
    values: IOptionSingleValue[];
    description?: string;
}> {
    const labelConfigReadonly =
        rootProduct.attributes.product_option_labels?.value[code] || [];
    const labelConfig = labelConfigReadonly.map((item) => ({
        label: item.label,
        values: [...item.values],
        description: item.description,
    }));
    if (labelConfig.length <= 0) {
        labelConfig.push({
            label: prefixNameCapped,
            values: [...options],
            description: undefined,
        });
    }
    return labelConfig;
}

function findFirstRenderableOption(
    labelConfig: Array<{ values: IOptionSingleValue[] }>,
    options: readonly IOptionSingleValue[],
): IOptionSingleValue | undefined {
    for (const labelGroup of labelConfig) {
        for (const option of labelGroup.values) {
            if (options.indexOf(option) !== -1) {
                return option;
            }
        }
    }
    return undefined;
}

function findFirstAvailableOption(
    labelConfig: Array<{ values: IOptionSingleValue[] }>,
    options: readonly IOptionSingleValue[],
    codeVariants: Record<string, InternalProduct[]>,
): IOptionSingleValue | undefined {
    for (const labelGroup of labelConfig) {
        for (const option of labelGroup.values) {
            if (options.indexOf(option) === -1) {
                continue;
            }
            const variant = preferAvailableVariant(codeVariants[option] ?? []);
            if (variant?.availability.is_available_to_buy) {
                return option;
            }
        }
    }
    return undefined;
}

function isThreeColumnDesign(code: IOptionCode): boolean {
    switch (code) {
        case OptionTypes.SIZE:
        case OptionTypes.COLOR:
        case OptionTypes.PROFILE:
        case OptionTypes.HEIGHT:
            return true;
        default:
            return false;
    }
}

function findIconImageURL(
    productAttributeOptionGroup: IProductAttributeOptionGroup | null,
    option: IOptionSingleValue,
): IImageURL | null {
    let iconImageURL = null;
    productAttributeOptionGroup?.options.forEach((o) => {
        if (o.option === option) {
            iconImageURL = o.icon_image_url;
        }
    });
    return iconImageURL;
}

export const ProductOptionRadioSelector = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const dispatch = useAppDispatch();
    const appSlug = usePageSetting("app_slug");

    const rootProductFromState = rootProductSelector(state);
    const productAttributeOptionGroup = productAttributeOptionGroupSelector(
        state,
        props.code,
    );
    const namespace = rootProductFromState
        ? rootProductFromState.product_class_slug
        : null;
    const optionValues = state.ui.optionValues;

    // Refs #21452 - zlebar
    // Overriding `props.onChange` to accommodate
    // tsi-sealy's Desired "Level" behavior
    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        e.preventDefault();

        if ("option_level" in optionValues) {
            // Build a more updated optionValues object
            const updatedOptionValues = {
                ...optionValues,
                [props.code]: e.currentTarget.value,
            };

            // Build our Level options
            const levelOptions = listPossibleOptionValues(
                "option_level",
                props.rootProduct,
            );
            const levelVariants = buildRelevantLevelVariants(
                levelOptions,
                updatedOptionValues,
                props.rootProduct,
                "option_level",
            );
            const levelColumns =
                levelVariants.length > 2
                    ? findLevelVariants(levelVariants, updatedOptionValues)
                    : levelVariants;

            // If the newly selected options doesn't come in the currently selected Level
            if (optionValues.option_level !== levelColumns[0].level) {
                if (namespace) {
                    // Change the Level
                    dispatch(
                        setOptionValue({
                            namespace,
                            code: "option_level",
                            index: 0,
                            totalNumValues: 1,
                            value: levelColumns[0].level,
                        }),
                    );
                }
            }
        }

        props.onChange(e);
    };

    const codeVariants = getCodeVariants(
        props.options,
        props.rootProduct,
        props.code,
        optionValues,
    );

    const labelConfig = getLabelConfig(
        props.rootProduct,
        props.code,
        props.prefixNameCapped,
        props.options,
    );

    // Prefer the first available option for the roving tab stop so a
    // keyboard user lands on a focusable input. `disabled` inputs cannot
    // receive focus, so picking an OOS option as the tab stop would skip
    // the entire group. Fall back to the first renderable option only
    // when all options are unavailable.
    const rovingTabStopOption: IOptionSingleValue | undefined =
        props.value !== undefined && props.options.indexOf(props.value) !== -1
            ? props.value
            : (findFirstAvailableOption(
                  labelConfig,
                  props.options,
                  codeVariants,
              ) ?? findFirstRenderableOption(labelConfig, props.options));

    // Render each label group
    const groups = labelConfig.map((labelGroup, groupIndex) => {
        // Are we rendering within a Panel?
        const isPanel = props.configuratorType === ConfiguratorTypes.PANEL;
        const threeColumn = isThreeColumnDesign(props.code);

        const optionBtns = labelGroup.values.map((option, index) => {
            // Make sure option is valid
            if (props.options.indexOf(option) === -1) {
                return null;
            }
            const optionContainerClass = classNames({
                "configurator__radio-option-container": true,
                [`configurator__radio-option-container--${props.selectorID}`]: true,
                [styles.twoColumnContainer]:
                    props.isPlpPanelStyle || (isPanel && !threeColumn),
                [styles.threeColumnContainer]:
                    !props.isPlpPanelStyle && isPanel && threeColumn,
            });
            const optionClass = classNames({
                "configurator__radio-option": true,
                [`configurator__radio-option--${props.selectorID}`]: true,
                [styles.vertical]:
                    isPanel &&
                    (props.code === OptionTypes.SIZE ||
                        props.code === OptionTypes.COLOR ||
                        props.code === OptionTypes.HEIGHT ||
                        props.code === OptionTypes.PROFILE),
                [styles.activeOption]: option === props.value,
                [styles.panelRadioOption]: isPanel,
                [styles.alternativePanelRadioOption]: props.isPlpPanelStyle,
            });
            const optionNameSuffix = `${props.rootProduct.id}-${props.selectorID}-${props.code}-${groupIndex}-${index}`;
            const optionName = `configurator__radio-option--${optionNameSuffix}`;
            const optionsGroupName = `${props.code}-${props.selectorID}`;
            const optionSelected = option === props.value;
            const isTabStop =
                rovingTabStopOption !== undefined &&
                option === rovingTabStopOption;

            const iconImageURL = findIconImageURL(
                productAttributeOptionGroup,
                option,
            );

            // 1-800-Pillows OOS treatment — disable the input for variants
            // that can't be bought so the browser blocks clicks and keyboard
            // selection. The visual "Not Available" cue is rendered via
            // buildUnavailableOverlay above. Gated by appSlug so other sites
            // keep the pre-existing always-enabled behavior.
            const optionVariant = preferAvailableVariant(
                codeVariants[option] ?? [],
            );
            const isUnavailable =
                appSlug === AppSlug.ONE800PILLOWS &&
                (optionVariant == null ||
                    !optionVariant.availability.is_available_to_buy);

            return (
                <li key={option} className={optionContainerClass}>
                    <input
                        type="radio"
                        id={optionName}
                        name={optionsGroupName}
                        tabIndex={isTabStop ? 0 : -1}
                        onChange={handleChange}
                        value={option}
                        checked={optionSelected}
                        disabled={isUnavailable}
                    />
                    <label htmlFor={optionName} className={optionClass}>
                        {buildLabelContent(
                            iconImageURL,
                            isPanel,
                            option,
                            props.isPlpPanelStyle,
                            buildUnavailableOverlay(isUnavailable) ??
                                buildPricingOverlay(
                                    optionVariant,
                                    props.showPricing,
                                ),
                        )}
                    </label>
                </li>
            );
        });

        const prefixClass = classNames({
            [props.prefixClass]: true,
            "ada-screenreader-only": labelConfig.length <= 1 && isPanel,
        });

        return (
            <fieldset key={labelGroup.label}>
                <legend className={prefixClass}>{labelGroup.label}</legend>
                {labelGroup.description && (
                    <p className={styles.description}>
                        {labelGroup.description}
                    </p>
                )}
                <ul className={props.configuratorClass}>{optionBtns}</ul>
            </fieldset>
        );
    });

    return (
        <div key={props.code} className="configurator__radio-container">
            {groups}
        </div>
    );
};
